import { Link } from 'react-router-dom';
import { Target, Eye, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: <Target className="w-8 h-8 text-blue-700" />,
      title: 'Strategic Focus',
      description: 'We provide tailored strategies that align with your business objectives and market realities.'
    },
    {
      icon: <Award className="w-8 h-8 text-blue-700" />,
      title: 'Proven Expertise',
      description: 'Deep industry knowledge and extensive network across Asia and global markets.'
    },
    {
      icon: <Eye className="w-8 h-8 text-blue-700" />,
      title: 'Long-term Partnership',
      description: 'We are committed to your sustained success through every stage of growth.'
    }
  ];

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              About Aquavera
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              A Singapore-based Cross-Border Growth Advisory firm dedicated to helping international companies succeed in Asia and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Mission</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Aquavera exists to bridge the gap between ambitious international companies and the vast opportunities across Asian and global markets.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We combine strategic insight, deep market knowledge, and an extensive network of partners to accelerate your cross-border growth. Whether you're a US company entering Asia, an Asian company expanding internationally, or a growth-stage business seeking strategic partnerships and capital, we provide the guidance and connections you need to succeed.
              </p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-12 border border-slate-200">
              <h3 className="text-2xl font-semibold text-slate-900 mb-6">Who We Serve</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">US companies entering Asia</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">Asian companies expanding internationally</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">DeepTech companies</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">Healthcare companies</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">AI companies</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">Family Offices</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-700 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-slate-700">SMEs and growth-stage businesses</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Approach</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Principles that guide our work and define our relationships with clients
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="mb-4 flex justify-center">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Singapore Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-6 text-center">Why Singapore?</h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Singapore is the undisputed gateway to Asia and increasingly to global markets. As a leading financial hub with world-class infrastructure, transparent regulatory frameworks, and a business-friendly environment, Singapore offers unparalleled advantages for companies seeking to expand internationally.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our Singapore base positions us at the heart of Asia's economic dynamism, enabling us to provide on-the-ground insights, facilitate strategic introductions, and help you navigate the complexities of cross-border expansion.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-700 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Let's Build Your Growth Strategy Together
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Discover how Aquavera can support your expansion goals.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-slate-50 font-semibold px-8 text-base"
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
